import React, { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, CircleX, Clock, ExternalLink, Minus, X, TriangleAlert, Zap } from "lucide-react";
import { fmtMs, fmtUsd, pct, preview, timeAgo, useQuery } from "../lib";
import { Badge, Button, JsonView, OutputView, PassBar, Seg, cx } from "../ui";
import { ExecutionDrawer } from "./logs";

export function EvalRunPage({ p, id }: { p: string; id: string }) {
  const [poll, setPoll] = useState(1500);
  const { data: run } = useQuery<any>("/api/evals/" + id, { poll: poll || undefined });
  const { data: project } = useQuery<any>("/api/projects/" + p);
  const [filter, setFilter] = useState<"all" | "fail" | "diff">("all");
  const [open, setOpen] = useState<{ caseId: string; cell: string } | null>(null);
  const [openEx, setOpenEx] = useState<string | null>(null);
  if (run && run.status !== "running" && poll) setTimeout(() => setPoll(0), 0);
  const cells: string[] = run?.config?.cells ?? [];
  const byKey = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const r of run?.results ?? []) {
      const k = r.caseId + "|" + r.cell;
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return m;
  }, [run]);
  if (!run) return <div className="content"><div className="page"><div className="skeleton" style={{ height: 28, width: 260 }} /></div></div>;
  const summary: any[] = run.summary ?? [];
  const best = [...summary].filter((s) => s.done).sort((a, b) => b.passed / b.done - a.passed / a.done || b.avgScore - a.avgScore)[0];
  const casePass = (caseId: string, cell: string) => {
    const rs = byKey.get(caseId + "|" + cell) ?? [];
    if (!rs.length) return null;
    return rs.filter((r) => r.pass).length / rs.length;
  };
  const cases = (run.cases as any[]).filter((c) => {
    const vals = cells.map((cell) => casePass(c.id, cell)).filter((v) => v !== null) as number[];
    if (filter === "fail") return vals.some((v) => v < 1);
    if (filter === "diff") return new Set(vals.map((v) => v === 1)).size > 1;
    return true;
  });
  const trials = run.config.trials ?? 1;
  const firstVar = (c: any) => preview(Object.values(c.vars ?? {})[0], 86);
  const targetName = project?.targets?.find((x: any) => x.slug === run.target)?.name ?? run.target;
  return (
    <>
      <div className="topbar">
        <div className="crumbs">
          <Link href={"/p/" + p}>{project?.name ?? p}</Link><span>/</span>
          <Link href={"/p/" + p + "/t/" + run.target + "/avaliacoes"}>{targetName}</Link><span>/</span>
          <span className="cur">Avaliação</span>
        </div>
        <div className="grow" />
        {run.status === "running" ? <span className="row faint mono" style={{ fontSize: 12 }}><span className="spinner" />{run.done}/{run.total}</span> : run.status === "error" ? <Badge tone="red">erro</Badge> : <Badge tone="green">concluída</Badge>}
      </div>
      <div className="content">
        <div className="page" style={{ maxWidth: 1280 }}>
          <div className="page-head">
            <div>
              <h1 className="page-title">Avaliação de {targetName}</h1>
              <div className="page-sub">
                {run.cases.length} casos · {cells.length} combinação(ões) · {trials} repetição(ões) · {timeAgo(run.createdAt)}
                {run.finishedAt ? " · levou " + fmtMs(run.finishedAt - run.createdAt) : ""}
              </div>
            </div>
          </div>
          {run.status === "running" && <div className="progress" style={{ marginBottom: 20 }}><div style={{ width: pct(run.done, run.total) + "%" }} /></div>}
          {run.error && <div className="banner"><TriangleAlert size={15} /><span className="grow">{run.error}</span></div>}

          <div className="grid-3" style={{ gridTemplateColumns: "repeat(" + Math.min(4, Math.max(1, summary.length)) + ", minmax(0, 1fr))" }}>
            {summary.map((s) => (
              <div key={s.cell} className={cx("card stat", best && s.cell === best.cell && summary.length > 1 && run.status === "done" && "best")}>
                <div className="between">
                  <span className="row" style={{ gap: 6 }}><Badge tone={s.version === "draft" ? "violet" : "plain"}>{s.version}</Badge><span className="mono faint ellipsis" style={{ fontSize: 11.5 }}>{s.model}</span></span>
                  {best && s.cell === best.cell && summary.length > 1 && run.status === "done" && <Badge tone="green">melhor</Badge>}
                </div>
                <div className="stat-value">{s.done ? pct(s.passed, s.done) : 0}%</div>
                <div style={{ margin: "6px 0 10px" }}><PassBar passed={s.passed} total={s.done || s.total} /></div>
                <div className="row wrap" style={{ gap: 6 }}>
                  <span className="chip">score {s.avgScore.toFixed(2).replace(".", ",")}</span>
                  <span className="chip">p50 {fmtMs(s.p50)}</span>
                  <span className="chip">{s.costKnown ? fmtUsd(s.costUsd) : fmtUsd(s.costUsd) + "+?"}</span>
                  {s.cacheHits > 0 && <span className="chip chip-green">{s.cacheHits} do cache</span>}
                  {s.errors > 0 && <span className="chip" style={{ color: "var(--red)" }}>{s.errors} erros</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="section card">
            <div className="card-head">
              <span className="card-title">Casos</span>
              <Seg value={filter} onChange={setFilter} options={[{ value: "all", label: "Todos" }, { value: "fail", label: "Com falha" }, ...(cells.length > 1 ? [{ value: "diff" as const, label: "Onde divergem" }] : [])]} />
            </div>
            <div className="table-wrap" style={{ borderRadius: 0 }}>
              <table className="table matrix">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Caso</th>
                    <th>Entrada</th>
                    <th>Esperado</th>
                    {cells.map((c) => {
                      const [v, ...m] = c.split("|");
                      return <th key={c} style={{ textAlign: "center" }}>{v}<div style={{ textTransform: "none", letterSpacing: 0, marginTop: 4, fontSize: 10.5 }}>{m.join("|").split("/").pop()}</div></th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c: any) => (
                    <tr key={c.id}>
                      <td className="mono faint" style={{ fontSize: 12 }}>{c.id}{c.tags?.includes("pegadinha") && <div><Badge tone="amber">pegadinha</Badge></div>}</td>
                      <td style={{ maxWidth: 360 }}><div className="ellipsis">{firstVar(c)}</div>{c.notes && <div className="faint ellipsis" style={{ fontSize: 12 }}>{c.notes}</div>}</td>
                      <td className="mono" style={{ fontSize: 12, maxWidth: 220 }}><div className="ellipsis">{c.expected === undefined ? "—" : preview(c.expected, 60)}</div></td>
                      {cells.map((cell) => {
                        const rs = byKey.get(c.id + "|" + cell) ?? [];
                        const passed = rs.filter((r) => r.pass).length;
                        const err = rs.some((r) => r.error);
                        const state = !rs.length ? "pending" : err && !passed ? "err" : passed === rs.length ? "pass" : "fail";
                        return (
                          <td key={cell} className="cellres">
                            <span className={cx("cellmark", state)} onClick={() => rs.length && setOpen({ caseId: c.id, cell })} title={rs.length ? "score " + (rs.reduce((s, r) => s + r.score, 0) / rs.length).toFixed(2) : "aguardando"}>
                              {state === "pass" ? <Check size={13} /> : state === "fail" ? (trials > 1 && passed ? <span className="mono" style={{ fontSize: 10.5 }}>{passed}/{rs.length}</span> : <X size={13} />) : state === "err" ? <TriangleAlert size={12} /> : <Minus size={12} />}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!cases.length && <div className="empty">Nenhum caso com esse filtro.</div>}
            </div>
          </div>
        </div>
      </div>
      {open && (
        <CaseView
          run={run}
          cases={cases.some((c: any) => c.id === open.caseId) ? cases : run.cases}
          cells={cells}
          byKey={byKey}
          open={open}
          onOpen={setOpen}
          onClose={() => setOpen(null)}
          onExecution={setOpenEx}
          blockKeys={!!openEx}
        />
      )}
      <ExecutionDrawer id={openEx} onClose={() => setOpenEx(null)} />
    </>
  );
}

const fmtScore = (n: number) => n.toFixed(2).replace(".", ",");

// Detalhe de um caso em tela cheia: entrada e esperado à esquerda, resultado de cada repetição à direita.
function CaseView({ run, cases, cells, byKey, open, onOpen, onClose, onExecution, blockKeys }: {
  run: any;
  cases: any[];
  cells: string[];
  byKey: Map<string, any[]>;
  open: { caseId: string; cell: string };
  onOpen: (v: { caseId: string; cell: string }) => void;
  onClose: () => void;
  onExecution: (id: string) => void;
  blockKeys: boolean;
}) {
  const idx = cases.findIndex((c) => c.id === open.caseId);
  const c = cases[idx] ?? run.cases.find((x: any) => x.id === open.caseId);
  const rows = byKey.get(open.caseId + "|" + open.cell) ?? [];
  const firstFail = rows.find((r) => !r.pass)?.trial ?? rows[0]?.trial ?? 1;
  const [trial, setTrial] = useState<number>(firstFail);
  useEffect(() => setTrial(firstFail), [open.caseId, open.cell]);
  const r = rows.find((x) => x.trial === trial) ?? rows[0];
  const go = (d: number) => {
    const next = cases[idx + d];
    if (next) onOpen({ caseId: next.id, cell: open.cell });
  };

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (blockKeys || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" || e.key === "k") go(-1);
      else if (e.key === "ArrowRight" || e.key === "j") go(1);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });
  // O painel de rolagem volta ao topo ao trocar de caso, combinação ou repetição.
  const [mainEl, setMainEl] = useState<HTMLElement | null>(null);
  const [sideEl, setSideEl] = useState<HTMLElement | null>(null);
  useEffect(() => { mainEl?.scrollTo({ top: 0 }); }, [mainEl, open.caseId, open.cell, trial]);
  useEffect(() => { sideEl?.scrollTo({ top: 0 }); }, [sideEl, open.caseId]);

  const passed = rows.filter((x) => x.pass).length;
  const [version, ...modelParts] = open.cell.split("|");
  const model = modelParts.join("|");
  if (!c) return null;
  return (
    <div className="cv" role="dialog" aria-modal="true" aria-label={"Caso " + c.id}>
      <header className="cv-head">
        <div className="row" style={{ gap: 2 }}>
          <Button variant="ghost" size="sm" icon={<ChevronLeft size={16} />} onClick={() => go(-1)} disabled={idx <= 0} title="Caso anterior (←)" />
          <Button variant="ghost" size="sm" icon={<ChevronRight size={16} />} onClick={() => go(1)} disabled={idx < 0 || idx >= cases.length - 1} title="Próximo caso (→)" />
        </div>
        <div className="row" style={{ gap: 10, minWidth: 0 }}>
          <span className="cv-id mono">{c.id}</span>
          {idx >= 0 && <span className="faint mono" style={{ fontSize: 12 }}>{idx + 1} de {cases.length}</span>}
          {c.tags?.includes("pegadinha") && <Badge tone="amber">pegadinha</Badge>}
          {rows.length > 0 && (
            <span className={cx("cv-verdict", passed === rows.length ? "pass" : "fail")}>
              {passed === rows.length ? <Check size={12} /> : <X size={12} />}
              {rows.length > 1 ? passed + "/" + rows.length + " passaram" : passed ? "passou" : "falhou"}
            </span>
          )}
        </div>
        <div className="grow" />
        {cells.length > 1 ? (
          <Seg
            value={open.cell}
            onChange={(cell) => onOpen({ caseId: open.caseId, cell })}
            options={cells.map((cell) => {
              const rs = byKey.get(open.caseId + "|" + cell) ?? [];
              const ok = rs.length > 0 && rs.every((x) => x.pass);
              const [v, ...m] = cell.split("|");
              return { value: cell, label: <span className="row" style={{ gap: 6 }}><span className={cx("cv-dot", !rs.length ? "" : ok ? "pass" : "fail")} />{v} · {m.join("|").split("/").pop()}</span> };
            })}
          />
        ) : (
          <span className="row" style={{ gap: 6, minWidth: 0 }}><Badge tone={version === "draft" ? "violet" : "plain"}>{version}</Badge><span className="mono faint ellipsis" style={{ fontSize: 12 }}>{model}</span></span>
        )}
        <span className="cv-keys"><kbd>←</kbd><kbd>→</kbd> casos <kbd>esc</kbd> fecha</span>
        <Button variant="ghost" size="sm" icon={<X size={16} />} onClick={onClose} title="Fechar (Esc)" />
      </header>

      <div className="cv-body">
        <aside className="cv-side" ref={setSideEl}>
          <section>
            <div className="label cv-label">Entrada</div>
            <div className="col" style={{ gap: 18 }}>
              {Object.entries(c.vars ?? {}).map(([k, v]) => (
                <div key={k}>
                  <div className="var-name" style={{ marginBottom: 6 }}>{k}</div>
                  {typeof v === "string" ? <div className="cv-text">{v}</div> : <JsonView value={v} />}
                </div>
              ))}
            </div>
          </section>
          <section>
            <div className="label cv-label">Esperado</div>
            {c.expected === undefined ? <span className="faint">sem esperado</span> : typeof c.expected === "string" ? <div className="cv-box cv-text">{c.expected}</div> : <JsonView value={c.expected} />}
            {c.notes && <div className="field-hint" style={{ marginTop: 10 }}>{c.notes}</div>}
          </section>
        </aside>

        <main className="cv-main" ref={setMainEl}>
          <div className="cv-inner">
            {!r ? (
              <div className="empty">Ainda sem resultado para esse caso.</div>
            ) : (
              <>
                {rows.length > 1 && (
                  <div className="cv-trials">
                    {rows.map((x) => (
                      <button key={x.trial} className={cx("cv-trial", x.trial === r.trial && "on")} onClick={() => setTrial(x.trial)}>
                        <span className={cx("cv-dot", x.pass ? "pass" : "fail")} />
                        Repetição {x.trial}
                        <span className="mono cv-trial-score">{fmtScore(x.score)}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="between" style={{ marginBottom: 14 }}>
                  <span className="row" style={{ gap: 10 }}>
                    {r.pass ? <CircleCheck size={20} color="var(--green)" /> : <CircleX size={20} color="var(--red)" />}
                    <span className="cv-status">{r.pass ? "Passou" : "Falhou"}</span>
                    <span className="chip">score {fmtScore(r.score)}</span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    {r.cacheHit ? <span className="chip chip-green"><Zap size={11} />cache</span> : <span className="chip"><Clock size={11} />{fmtMs(r.latencyMs)}</span>}
                    {r.executionId && <Button size="sm" icon={<ExternalLink size={13} />} onClick={() => onExecution(r.executionId)}>Execução</Button>}
                  </span>
                </div>

                <section className="cv-out">
                  <div className="between" style={{ marginBottom: 10 }}>
                    <span className="label">Saída</span>
                    {typeof r.output === "string" && <span className="faint mono" style={{ fontSize: 11.5 }}>{r.output.length} caracteres</span>}
                  </div>
                  {r.error ? <div className="cv-text" style={{ color: "var(--red)" }}>{r.error}</div> : typeof r.output === "string" ? <div className="cv-text cv-text-lg">{r.output}</div> : <OutputView format="json" value={r.output} />}
                </section>

                <div className="between" style={{ margin: "30px 0 12px" }}>
                  <span className="label">Avaliadores</span>
                  <span className="faint mono" style={{ fontSize: 11.5 }}>{r.verdicts.filter((v: any) => v.pass).length}/{r.verdicts.length} passaram</span>
                </div>
                <div className="col" style={{ gap: 10 }}>
                  {r.verdicts.map((v: any) => <VerdictCard key={open.caseId + open.cell + r.trial + v.grader} v={v} />)}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

const graderType: Record<string, string> = { regex: "regex", judge: "juiz", contains: "contém", equals: "igual", json_fields: "campos", json_schema: "schema", not_empty: "não vazio" };

function VerdictCard({ v }: { v: any }) {
  const checks: any[] = v.checks ?? [];
  const isRegex = v.type === "regex";
  const [showPattern, setShowPattern] = useState(false);
  return (
    <div className={cx("cv-grader", !v.pass && "fail")}>
      <div className="cv-grader-head">
        <span className={cx("cv-mark", v.pass ? "pass" : "fail")}>{v.pass ? <Check size={12} /> : <X size={12} />}</span>
        <span className="cv-grader-name">{v.name}</span>
        <span className="cv-tag">{graderType[v.type] ?? v.type}</span>
        <span className="grow" />
        {checks.length > 0 && <span className="faint mono" style={{ fontSize: 11.5 }}>{checks.filter((x) => x.pass).length}/{checks.length}</span>}
        {v.score > 0 && v.score < 1 && <span className="chip">score {fmtScore(v.score)}</span>}
      </div>
      {v.detail && (isRegex ? (
        <button className={cx("cv-pattern mono", showPattern && "open")} onClick={() => setShowPattern(!showPattern)} title={showPattern ? "Recolher" : "Ver o padrão inteiro"}>{v.detail}</button>
      ) : (
        <div className="cv-detail">{v.detail}</div>
      ))}
      {checks.length > 0 && (
        <div className="cv-checks">
          {checks.map((ck, i) => <CheckRow key={i} ck={ck} judge={v.type === "judge"} />)}
        </div>
      )}
    </div>
  );
}

function CheckRow({ ck, judge }: { ck: any; judge: boolean }) {
  const [open, setOpen] = useState(!ck.pass);
  const has = !!ck.detail;
  return (
    <div className={cx("cv-check", !ck.pass && "fail")}>
      <button className="cv-check-head" onClick={() => has && setOpen(!open)} disabled={!has} aria-expanded={has ? open : undefined}>
        <span className={cx("cv-mark sm", ck.pass ? "pass" : "fail")}>{ck.pass ? <Check size={11} /> : <X size={11} />}</span>
        <span className={cx("cv-check-name", !judge && "mono")}>{ck.name}</span>
        {has && <ChevronDown size={14} className={cx("cv-chev", open && "open")} />}
      </button>
      {has && open && <div className="cv-reason">{ck.detail}</div>}
    </div>
  );
}
